import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useFormik } from 'formik';
import * as yup from 'yup';
import * as _ from 'lodash';
import { useEffect } from 'react';
import { CommonButton, CommonConfirmationMessage, CommonTextField } from 'gemlux-common-components';
import { styled as materialStyled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate } from 'react-router-dom';
import { validation } from '../../../schema/validation';
import { theme } from '../../../theme/theme';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { generalActions } from '../../../redux/general/generalSlice';
import { RootState } from '../../../redux/store';
import LogoLink from '../../LogoLink/LogoLink';

const CustomButton = materialStyled(LoadingButton)(() => ({
  borderRadius: '8px',
  padding: '8px 12px',
  boxShadow: '0px 12px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px -2px 2px 0px rgba(48, 48, 48, 0.10) inset',
  alignSelf: 'stretch',
}));

function ContactUs() {
  const dispatch = useAppDispatch();
  const componentStatus = useAppSelector((state: RootState) => state.general.componentStatus);
  const isSendMessageSuccess = _.get(componentStatus, 'isSendMessageSuccess', undefined);
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;
  const error = _.get(componentStatus, 'error', undefined) as string;
  const navigate = useNavigate();

  useEffect(
    () => () => {
      dispatch(generalActions.setComponentStatus({ isSendMessageSuccess: undefined }));
    },
    [],
  );

  const formik = useFormik({
    initialValues: {
      firstName: '',
      familyName: '',
      businessEmail: '',
      message: '',
    },
    validationSchema: yup.object({
      firstName: validation.firstName,
      familyName: validation.familyName,
      businessEmail: validation.email,
      message: validation.message,
    }),
    onSubmit: ({ firstName, familyName, businessEmail, message }) => {
      dispatch(generalActions.setComponentStatus({ isSubmitting: true, error: undefined }));
      dispatch(
        generalActions.sendContactUsMessage({
          firstName,
          familyName,
          email: businessEmail,
          message,
        }),
      );
    },
  });

  const handleDismiss = () => {
    if (window.opener) {
      window.close();
    }
    navigate('/login');
  };

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      formik.handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, []);

  if (isSendMessageSuccess) {
    return (
      <CommonConfirmationMessage
        title="Message Sent!"
        description="We'll get in touch with you soon."
        button="Ok"
        onClick={handleDismiss}
      />
    );
  }

  return (
    <Grid
      display="flex"
      gap={6}
      flexDirection="column"
      sx={{
        maxWidth: '490px',
        backgroundColor: '#FFFFFF',
        padding: '5%',
        borderRadius: '2%',
        border: '1px solid #DCE0E5',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
        <LogoLink width="173.343px" height="32px" />
        <Typography variant="textH3" textAlign="center">
          Contact Us
        </Typography>
      </Box>
      <Grid container spacing="20px">
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="first-name">
            First Name
          </Typography>
          <CommonTextField
            id="first-name"
            type="text"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="family-name">
            Last Name
          </Typography>
          <CommonTextField
            id="family-name"
            type="text"
            name="familyName"
            value={formik.values.familyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.familyName && Boolean(formik.errors.familyName)}
            helperText={formik.touched.familyName && formik.errors.familyName}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="business-email">
            Email Address
          </Typography>
          <CommonTextField
            id="business-email"
            type="email"
            name="businessEmail"
            value={formik.values.businessEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.businessEmail && Boolean(formik.errors.businessEmail)}
            helperText={formik.touched.businessEmail && formik.errors.businessEmail}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
          />
        </Grid>
        <Grid item xs={12} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="message">
            Your Message
          </Typography>
          <CommonTextField
            id="message"
            type="text"
            name="message"
            value={formik.values.message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.message && Boolean(formik.errors.message)}
            helperText={formik.touched.message && formik.errors.message}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            multiline
            rows={4}
            errorTextColor="#DF0C3D"
            errorBorderColor="#DF0C3D"
            inputProps={{ maxLength: 2000 }}
          />
          <Typography
            variant="textXS"
            sx={{ alignSelf: 'flex-end', color: theme.palette.text.secondary, marginTop: 0.3 }}
          >
            {2000 - formik.values.message.length}/2000
          </Typography>
        </Grid>
      </Grid>
      {error && (
        <Typography variant="footnoteRegular" color="error" textAlign="center">
          {_.capitalize(error)}
        </Typography>
      )}
      <Grid item xs={12} display="flex" justifyContent="center">
        <CustomButton
          variant="contained"
          onClick={() => formik.handleSubmit()}
          loading={isSubmitting}
          sx={{
            backgroundColor: '#3348FF',
            '&:hover': {
              backgroundColor: '#007AFF',
            },
            width: '100%',
          }}
        >
          Send Message
        </CustomButton>
      </Grid>
    </Grid>
  );
}

export default ContactUs;
